import { useDispatch, useSelector } from "react-redux"
import { setConnectModal } from "../../ReduxSlices/ModalSlice"
import { useLocation, useNavigate } from "react-router-dom"
import { setConnect, setEthereum, setBech32Address } from "../../ReduxSlices/AuthSlice"
import { useEffect, useState, useCallback } from "react"
import { getChainId, getKeplrChainConfig, getNetworkName as getNetworkLabel } from "../../config/network"
import { toast } from "react-toastify"
import { api } from "../functions/Api"
import { X } from "lucide-react"
import { Logo } from "../../AssetComponents/Images"
import { isMobileDevice } from "../../utils/deviceDetection"
import { ensureEstateReferral, ensureEstateReferralCosmos } from "../../evm/services/referralService"
import { evmToBech32Address } from "../../evm/services/EvmContract"
import { connectSocial } from "../../utils/web3auth"
import {
  RDNS,
  getDiscoveredWallets,
  getProviderForWallet,
  setSelectedProvider,
  subscribeToWalletChanges,
  isMetaMaskAvailable,
  isRabbyAvailable,
  isCoinbaseAvailable,
  isTrustAvailable,
  isOkxAvailable,
} from "../../utils/eip6963"

// ── Icon URLs ──
const ICONS = {
  metamask: "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg",
  rabby: "https://app.sai.fun/icons/rabby.svg",
  coinbase: "https://app.sai.fun/icons/coinbase.svg",
  trust: "https://app.sai.fun/icons/trustwallet.svg",
  walletconnect: "https://app.sai.fun/icons/wallet-connect.svg",
  okx: "https://app.sai.fun/icons/okx.svg",
  keplr: "https://app.sai.fun/icons/keplr.svg",
  keplrMobile: "https://app.sai.fun/icons/keplrmobileibc.svg",
  cosmostation: "https://app.sai.fun/icons/cosmostation.svg",
}

// Social icon components
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
)
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#202020"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
)

// ── Social/misc icons for connecting state ──
const CONNECTING_ICONS = {
  google: "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg",
  x: "https://abs.twimg.com/favicons/twitter.3.ico",
  twitter: "https://abs.twimg.com/favicons/twitter.3.ico",
  apple: "https://images.web3auth.io/login-apple-light.svg",
  discord: "https://images.web3auth.io/login-discord-light.svg",
  facebook: "https://images.web3auth.io/login-facebook-light.svg",
  reddit: "https://images.web3auth.io/login-reddit-light.svg",
  github: "https://images.web3auth.io/login-github-light.svg",
  twitch: "https://images.web3auth.io/login-twitch-light.svg",
  wechat: "https://images.web3auth.io/login-wechat-light.svg",
  line: "https://images.web3auth.io/login-line-light.svg",
  kakao: "https://images.web3auth.io/login-kakao-light.svg",
  farcaster: "https://images.web3auth.io/login-farcaster-light.svg",
}

// ── EVM wallet definitions ──
const EVM_WALLETS = [
  { id: "metamask", name: "MetaMask", rdns: RDNS.METAMASK, icon: ICONS.metamask, detect: isMetaMaskAvailable },
  { id: "rabby", name: "Rabby", rdns: RDNS.RABBY, icon: ICONS.rabby, detect: isRabbyAvailable },
  { id: "coinbase", name: "Coinbase Wallet", rdns: RDNS.COINBASE, icon: ICONS.coinbase, detect: isCoinbaseAvailable },
  { id: "trust", name: "Trust Wallet", rdns: RDNS.TRUST, icon: ICONS.trust, detect: isTrustAvailable },
  { id: "okx", name: "OKX Wallet", rdns: RDNS.OKX, icon: ICONS.okx, detect: isOkxAvailable },
]

// Main wallet list as shown in the modal: Keplr sits right below MetaMask.
// Keplr is a Cosmos wallet (routed to handleKeplr), so it carries an isKeplr flag
// instead of the EVM detect/rdns fields.
const MAIN_WALLETS = [
  EVM_WALLETS[0], // MetaMask
  { id: "keplr", name: "Keplr", icon: ICONS.keplr, isKeplr: true },
  ...EVM_WALLETS.slice(1), // Rabby, Coinbase, Trust, OKX
]

// ── "View more" items ──
const MORE_WALLETS = [
  { id: "walletconnect", name: "WalletConnect", icon: ICONS.walletconnect, type: "evm" },
  { id: "cosmostation", name: "Cosmostation", icon: ICONS.cosmostation, type: "ibc" },
  { id: "keplrMobile", name: "Keplr Mobile", icon: ICONS.keplrMobile, type: "ibc" },
  { id: "reddit", name: "Reddit", icon: "https://images.web3auth.io/login-reddit-active.svg", type: "social" },
  { id: "apple", name: "Apple", icon: "https://images.web3auth.io/login-apple-active.svg", type: "social" },
  { id: "discord", name: "Discord", icon: "https://images.web3auth.io/login-discord-active.svg", type: "social" },
  { id: "wechat", name: "WeChat", icon: "https://images.web3auth.io/login-wechat-active.svg", type: "social" },
  { id: "facebook", name: "Facebook", icon: "https://images.web3auth.io/login-facebook-active.svg", type: "social" },
  { id: "line", name: "Line", icon: "https://images.web3auth.io/login-line-active.svg", type: "social" },
  { id: "github", name: "GitHub", icon: "https://images.web3auth.io/login-github-active.svg", type: "social" },
  { id: "twitch", name: "Twitch", icon: "https://images.web3auth.io/login-twitch-active.svg", type: "social" },
  { id: "kakao", name: "Kakao", icon: "https://images.web3auth.io/login-kakao-active.svg", type: "social" },
  { id: "farcaster", name: "Farcaster", icon: "https://images.web3auth.io/login-farcaster-active.svg", type: "social" },
]

// ── Keplr connect ──
const connectKeplrWallet = async () => {
  const chainId = getChainId()
  try {
    await window.keplr.enable(chainId)
  } catch (error) {
    if (error.message.includes("chain is not supported") || error.message.includes("There is no modular chain info")) {
      await window.keplr.experimentalSuggestChain(getKeplrChainConfig())
      await new Promise((r) => setTimeout(r, 2000))
      await window.keplr.enable(chainId)
    } else throw error
  }
  const offlineSigner = window.keplr.getOfflineSigner(chainId)
  const accounts = await offlineSigner.getAccounts()
  if (!accounts?.length) throw new Error("No accounts found in Keplr")
  return accounts[0].address
}

// ── EVM chain switch ──
const ensureNibiruChain = async (provider) => {
  // Compare lowercased — eth_chainId returns lowercase ("0x1af4") while the env
  // value may be uppercase ("0x1AF4"); a strict === would miss the match and force
  // an unnecessary switch/add even when already on Nibiru.
  const target = (process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID || "0x1AF4").toLowerCase()
  const currentChain = async () =>
    String(await provider.request({ method: "eth_chainId" })).toLowerCase()

  if ((await currentChain()) === target) return

  try {
    await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: target }] })
    return
  } catch (err) {
    const code = err?.code ?? err?.data?.originalError?.code
    const chainMissing =
      code === 4902 || String(err?.message || "").toLowerCase().includes("unrecognized chain")
    if (!chainMissing) {
      // Switch failed for another reason (e.g. -32603). The network almost certainly
      // already exists — re-check and treat a match as success. Do NOT attempt to add
      // it: adding a network whose RPC is already registered throws MetaMask's
      // "Could not add network that points to same RPC endpoint" error.
      if ((await currentChain()) === target) return
      throw err
    }
  }

  // Chain is genuinely not in the wallet yet → add it (this also switches to it).
  try {
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [{ chainId: target, chainName: "Nibiru", nativeCurrency: { name: "NIBI", symbol: "NIBI", decimals: 18 }, rpcUrls: [process.env.REACT_APP_NIBIRU_EVM_RPC || "https://evm-rpc.nibiru.fi"], blockExplorerUrls: [process.env.REACT_APP_NIBIRU_EVM_EXPLORER || "https://nibiscan.io/"] }],
    })
  } catch (addErr) {
    // "same RPC endpoint" / already-exists → not fatal. Re-check, then try one more
    // switch; only surface the error if we still aren't on the target chain.
    if ((await currentChain()) === target) return
    try {
      await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: target }] })
      return
    } catch {
      throw addErr
    }
  }
}

// ── Spinner ──
const Spinner = () => (
  <div className="w-5 h-5 border-2 border-[#5b1dee] border-t-transparent rounded-full animate-spin" />
)

// ── WalletRow ──
const WalletRow = ({ icon, name, detected, connecting, disabled, onClick }) => {
  const [shake, setShake] = useState(false)
  const handleClick = () => {
    if (disabled) { setShake(false); window.requestAnimationFrame(() => setShake(true)); return }
    onClick?.()
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      onAnimationEnd={() => setShake(false)}
      className={`flex items-center justify-between w-full px-4 py-3 rounded-[12px] transition-all duration-150 ${
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-[#f8f5ff] hover:shadow-[2px_2px_6px_0px_rgba(187,195,206,0.4),-2px_-2px_4px_0px_rgba(253,255,255,0.6)]"
      } bg-white shadow-[1px_1px_4px_0px_rgba(187,195,206,0.5),-1px_-1px_3px_0px_rgba(253,255,255,0.7)] ${
        shake ? "animate-[shake_260ms_ease-in-out]" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          {connecting ? <Spinner /> : (
            typeof icon === "string"
              ? <img src={icon} alt={name} className="w-8 h-8 rounded-lg object-contain" />
              : icon
          )}
        </div>
        <span className="text-[#202020] font-medium text-[15px]">{name}</span>
      </div>
      {detected && !connecting && (
        <span className="px-2 py-0.5 rounded-[8px] bg-[#f0fdf4] border border-[#d1fae5] text-[#10b981] text-xs font-semibold">
          Detected
        </span>
      )}
    </button>
  )
}

// ── MoreItem (2-col grid) ──
const MoreItem = ({ icon, name, connecting, disabled, onClick }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] transition-all ${
      disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-[#f8f5ff]"
    } bg-white shadow-[1px_1px_3px_0px_rgba(187,195,206,0.4),-1px_-1px_2px_0px_rgba(253,255,255,0.6)]`}
  >
    <div className="w-7 h-7 rounded-lg bg-[#f0f0f0] flex items-center justify-center shrink-0 overflow-hidden">
      {connecting ? <Spinner /> : (
        typeof icon === "string"
          ? <img src={icon} alt={name} className="w-7 h-7 rounded-lg object-contain" />
          : typeof icon === "function" ? icon() : icon
      )}
    </div>
    <span className="text-[#202020] font-medium text-[13px] truncate">{name}</span>
  </button>
)

// ── Transaction Modal (disabled) ──
export const TransactionModal = () => null

// ═══════════════════════════════════════════
// ── ConnectModal ──
// ═══════════════════════════════════════════
export const ConnectModal = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const open = useSelector((s) => s.modal.connectModal)
  const account_ = useSelector((s) => s.auth.account)
  const location = useLocation()

  const [connectingWallet, setConnectingWallet] = useState(null)
  const [showMore, setShowMore] = useState(false)
  const [detected, setDetected] = useState({})
  const [showRabbyInstructions, setShowRabbyInstructions] = useState(false)

  // Web3Auth social login provider mapping
  const SOCIAL_PROVIDER_MAP = {
    google: "google",
    x: "twitter",
    twitter: "twitter",
    apple: "apple",
    discord: "discord",
    facebook: "facebook",
    reddit: "reddit",
    github: "github",
    twitch: "twitch",
    wechat: "wechat",
    line: "line",
    kakao: "kakao",
    farcaster: "farcaster",
  }

  // Refresh detection
  const refreshDetection = useCallback(() => {
    const m = {}
    EVM_WALLETS.forEach((w) => { m[w.id] = w.detect() })
    setDetected(m)
  }, [])

  useEffect(() => {
    if (!open) return
    refreshDetection()
    const unsub = subscribeToWalletChanges(() => refreshDetection())
    return unsub
  }, [open, refreshDetection])

  const close = () => {
    if (connectingWallet) return
    dispatch(setConnectModal(false))
    setConnectingWallet(null)
    setShowMore(false)
    setShowRabbyInstructions(false)
    if (!account_) navigate(location.pathname)
  }

  useEffect(() => { if (account_ && open) close() }, [account_])

  // Body scroll lock
  useEffect(() => {
    if (open) { document.body.style.overflow = "hidden" }
    else { document.body.style.overflow = "" }
    return () => { document.body.style.overflow = "" }
  }, [open])

  // ── Backend helpers ──
  const connectToBackend = async (addr) => { try { return await api("user/login", { account: addr }) } catch { return null } }
  const trackConnect = async (addr, type) => { try { await api("tracking/wallet-connect", { walletAddress: addr, walletType: type }) } catch {} }
  const applyReferral = async (evmAddr) => {
    try {
      if (!evmAddr?.startsWith("0x") || !window.ethereum) return
      const { ethers } = await import("ethers")
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const result = await ensureEstateReferral(signer, evmAddr)
      // Referral toast only if a non-default code was applied (skip generic "Connected" — handled by caller)
      if (result.status === "redeemed" && result.code && result.code !== "ESTATE") {
        toast.success(`Referral code ${result.code} applied!`, { autoClose: 3000 })
      }
    } catch {}
  }

  // Cosmos (Keplr/Leap) referral enforcement — ensure native wallets are on ESTATE too.
  const applyReferralCosmos = async (nibiAddr, walletType = "keplr") => {
    try {
      if (!nibiAddr?.startsWith("nibi")) return
      await ensureEstateReferralCosmos(nibiAddr, walletType, dispatch)
    } catch {}
  }

  // ── Web3Auth social login ──
  const handleSocialLogin = async (providerId = "google") => {
    const loginProvider = SOCIAL_PROVIDER_MAP[providerId] || providerId
    if (connectingWallet) return
    setConnectingWallet(providerId)
    try {
      const { address } = await connectSocial(loginProvider)
      if (!address) throw new Error("No address returned")

      dispatch(setConnect({ account: address, wallet: "web3auth" }))
      dispatch(setEthereum(address))
      await connectToBackend(address)
      trackConnect(address, `web3auth-${loginProvider}`)
      applyReferral(address)

      // Resolve bech32
      evmToBech32Address(address)
        .then((b) => { dispatch(setBech32Address(b)); window.__nibiru_bech32 = b })
        .catch(() => {})

      toast.success("Successfully connected!", { autoClose: 3000, toastId: "wallet-connected" })
      dispatch(setConnectModal(false))
    } catch (error) {
      const msg = (error?.message || "")
      if (msg === "USER_CANCELLED") {
        toast.info("Wallet connection declined", { autoClose: 3000 })
      } else {
        toast.error(`Login failed: ${msg || "Unknown error"}`)
      }
    } finally {
      setConnectingWallet(null)
    }
  }

  // ── EVM connect ──
  const handleEvmConnect = async (w) => {
    if (connectingWallet) return
    setConnectingWallet(w.id)
    try {
      // Mobile MetaMask redirect
      if (isMobileDevice() && w.id === "metamask" && !(window.ethereum?.isMetaMask)) {
        toast.info("Opening MetaMask app...", { autoClose: 5000 })
        const url = window.location.pathname === "/earn"
          ? "https://metamask.app.link/dapp/app.codedestate.com/earn"
          : "https://metamask.app.link/dapp/app.codedestate.com/perps/markets/"
        setTimeout(() => { window.location.href = url }, 500)
        return
      }

      let provider = w.rdns ? getProviderForWallet(w.rdns) : null
      if (!provider && w.id === "metamask") provider = window.ethereum

      if (!provider) {
        const links = { rabby: "https://rabby.io/", coinbase: "https://www.coinbase.com/wallet/downloads", trust: "https://trustwallet.com/download", okx: "https://www.okx.com/web3", metamask: "https://metamask.io" }
        toast.error(<span>{w.name} is not installed. <a href={links[w.id] || "#"} target="_blank" rel="noopener noreferrer" className="underline text-[#5b1dee]">Install {w.name}</a></span>)
        return
      }

      setSelectedProvider(provider)
      const accounts = await provider.request({ method: "eth_requestAccounts" })
      if (!accounts?.length) throw new Error("No accounts returned")

      // Rabby unrecognized chain handling
      try {
        await ensureNibiruChain(provider)
      } catch (chainErr) {
        const msg = (chainErr?.message || "").toLowerCase()
        if (w.id === "rabby" && (msg.includes("unrecognized chain") || msg.includes("user rejected"))) {
          setShowRabbyInstructions(true)
          return
        }
        throw chainErr
      }

      const addr = accounts[0]
      dispatch(setConnect({ account: addr, wallet: w.id }))
      dispatch(setEthereum(addr))
      await connectToBackend(addr)
      trackConnect(addr, w.id)
      applyReferral(addr)
      evmToBech32Address(addr).then((b) => { dispatch(setBech32Address(b)); window.__nibiru_bech32 = b }).catch(() => {})
      toast.success("Connected to Nibiru EVM", { autoClose: 3000, toastId: "wallet-connected" })
      dispatch(setConnectModal(false))
    } catch (error) {
      const msg = error?.message || ""
      if (msg.includes("rejected") || msg.includes("User rejected")) toast.error("Connection rejected by user")
      else if (msg.includes("pending") || error?.code === -32002) toast.error("A connection request is already pending. Check your wallet.")
      else toast.error(`Cannot connect to ${w.name}: ${msg}`)
    } finally { setConnectingWallet(null) }
  }

  // ── Keplr ──
  const handleKeplr = async () => {
    if (connectingWallet) return
    setConnectingWallet("keplr")
    try {
      if (isMobileDevice()) {
        if (window.keplr && (window.keplr.mode === "mobile-web" || window.keplr.mode === "extension")) {
          const addr = await connectKeplrWallet()
          dispatch(setConnect({ account: addr, wallet: "keplr" }))
          await connectToBackend(addr); trackConnect(addr, "keplr")
          applyReferralCosmos(addr, "keplr")
          toast.success(`Connected to ${getNetworkLabel()}`, { autoClose: 3000, toastId: "wallet-connected" })
          dispatch(setConnectModal(false))
        } else toast.info("Type in app.codedestate.com in the Keplr Wallet App built-in Browser to proceed", { autoClose: 8000 })
      } else {
        if (!window.keplr) { toast.error("Keplr wallet is not installed."); return }
        const addr = await connectKeplrWallet()
        dispatch(setConnect({ account: addr, wallet: "keplr" }))
        await connectToBackend(addr); trackConnect(addr, "keplr")
        applyReferralCosmos(addr, "keplr")
        toast.success(`Connected to ${getNetworkLabel()}`, { autoClose: 3000, toastId: "wallet-connected" })
        dispatch(setConnectModal(false))
      }
    } catch (e) { toast.error(`Keplr error: ${e.message}`) }
    finally { setConnectingWallet(null) }
  }

  // ── More wallet click router ──
  const handleMoreClick = (item) => {
    if (item.id === "keplr" || item.id === "keplrMobile") handleKeplr()
    else if (item.id === "walletconnect") toast.info("WalletConnect coming soon!")
    else if (item.id === "cosmostation") toast.info("Cosmostation support coming soon!")
    else handleSocialLogin(item.id) // Web3Auth social login with specific provider
  }

  if (!open) return null

  const isConnecting = !!connectingWallet

  return (
    <>
      {/* Shake keyframe */}
      <style>{`
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-4px)}40%,80%{transform:translateX(4px)}}
        .wallet-modal-scroll::-webkit-scrollbar{width:4px}
        .wallet-modal-scroll::-webkit-scrollbar-track{background:transparent}
        .wallet-modal-scroll::-webkit-scrollbar-thumb{background:#e0e0e0;border-radius:4px}
        .wallet-modal-scroll::-webkit-scrollbar-thumb:hover{background:#ccc}
      `}</style>

      <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4" onClick={close}>
        <div
          className="w-full max-w-[460px] max-h-[90vh] rounded-[16px] bg-white shadow-[4px_4px_16px_0px_rgba(187,195,206,0.5),-4px_-4px_12px_0px_rgba(253,255,255,0.8)] overflow-y-auto wallet-modal-scroll animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Connecting state ── */}
          {isConnecting ? (
            <div className="relative flex flex-col items-center gap-6 py-10 px-6">
              <button onClick={() => { setConnectingWallet(null) }} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-[#f0f0f0]">
                <X className="w-4 h-4 text-[#8A8A8A]" />
              </button>
              <div className="text-center">
                <h2 className="text-[20px] font-bold text-[#202020]">Connecting wallet...</h2>
                <p className="text-[13px] text-[#8A8A8A] mt-1">Please confirm in your wallet extension</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img src={
                    EVM_WALLETS.find((w) => w.id === connectingWallet)?.icon
                    || MORE_WALLETS.find((w) => w.id === connectingWallet)?.icon
                    || ICONS[connectingWallet]
                    || CONNECTING_ICONS[connectingWallet]
                    || ICONS.metamask
                  } alt="" className="w-10 h-10" />
                </div>
                <div className="flex gap-1.5">
                  {[0, 150, 300, 450].map((d) => (
                    <div key={d} className="w-1.5 h-1.5 rounded-full bg-[#5b1dee] animate-pulse" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
                <Logo />
              </div>
            </div>
          ) : (
            /* ── Main view ── */
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-2">
                <h2 className="text-[18px] font-bold text-[#202020]">Connect wallet</h2>
                <button onClick={close} className="p-1.5 rounded-lg hover:bg-[#f0f0f0] transition-colors"><X className="w-5 h-5 text-[#8A8A8A]" /></button>
              </div>

              {/* Social login buttons */}
              <div className="px-5 pb-4">
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => handleSocialLogin("google")}
                    className="flex items-center justify-center gap-2.5 px-4 py-3 rounded-[12px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.5),-2px_-2px_4px_0px_rgba(253,255,255,0.7)] hover:bg-[#f8f5ff] transition-colors text-[#202020] text-sm font-medium">
                    <GoogleIcon /> Log in with Google
                  </button>
                  <button onClick={() => handleSocialLogin("x")}
                    className="flex items-center justify-center gap-2.5 px-4 py-3 rounded-[12px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.5),-2px_-2px_4px_0px_rgba(253,255,255,0.7)] hover:bg-[#f8f5ff] transition-colors text-[#202020] text-sm font-medium">
                    <XIcon /> Log in with X
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-[#f0f0f0]" />

              {/* Rabby instructions */}
              {showRabbyInstructions && (
                <div className="mx-5 mt-3 p-4 rounded-xl border border-[#e5d5ff] bg-[#faf5ff]">
                  <h3 className="font-bold text-[14px] text-[#202020] mb-1">Add Nibiru network in Rabby</h3>
                  <p className="text-[12px] text-[#8A8A8A] mb-2">Rabby cannot add custom networks automatically. Please add manually:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-[12px] text-[#333]">
                    <li>Open Rabby &rarr; <b>Gear Icon</b> &rarr; <b>Add Custom Network</b></li>
                    <li>Chain ID: <b>6900</b></li>
                    <li>Network name: <b>Nibiru</b></li>
                    <li>RPC URL: <b>https://evm-rpc.nibiru.fi</b></li>
                    <li>Currency: <b>NIBI</b></li>
                    <li>Explorer: <b>https://nibiscan.io</b></li>
                  </ol>
                  <button onClick={() => setShowRabbyInstructions(false)} className="mt-2 px-3 py-1 rounded-lg bg-[#5b1dee] text-white text-xs font-medium hover:bg-[#4a15c7]">Got it</button>
                </div>
              )}

              {/* Main wallet list (MetaMask, Keplr, then the rest) */}
              <div className="px-5 py-3 space-y-2">
                {MAIN_WALLETS.map((w) => (
                  <WalletRow
                    key={w.id}
                    icon={(() => {
                      // Prefer EIP-6963 icon (data URI from extension)
                      const disc = w.rdns ? getDiscoveredWallets().find((d) => d.info.rdns === w.rdns) : null
                      return disc?.info?.icon || w.icon
                    })()}
                    name={w.name}
                    detected={detected[w.id]}
                    connecting={connectingWallet === w.id}
                    disabled={isConnecting && connectingWallet !== w.id}
                    onClick={() => w.isKeplr ? handleKeplr() : handleEvmConnect(w)}
                  />
                ))}
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-[#f0f0f0]" />

              {/* View more wallets */}
              <div className="px-5 py-3">
                <button onClick={() => setShowMore(!showMore)} className="flex items-center justify-between w-full">
                  <span className="text-[13px] text-[#8A8A8A]">View more wallets</span>
                  <div className="flex items-center gap-2">
                    {/* Preview avatars */}
                    <div className="flex -space-x-1.5">
                      {[ICONS.walletconnect, ICONS.cosmostation, ICONS.keplrMobile].map((src, i) => (
                        <div key={i} className="w-5 h-5 rounded-full border-2 border-white overflow-hidden bg-[#f0f0f0]">
                          <img src={src} alt="" className="w-full h-full object-contain" />
                        </div>
                      ))}
                    </div>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform ${showMore ? "rotate-180" : ""}`}>
                      <path d="M2 4L5 7L8 4" stroke="#8A8A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>
              </div>

              {/* More wallets grid */}
              {showMore && (
                <div className="px-5 pb-4 animate-fadeIn">
                  <div className="grid grid-cols-2 gap-2">
                    {MORE_WALLETS.map((item) => (
                      <MoreItem
                        key={item.id}
                        icon={item.icon}
                        name={item.name}
                        connecting={connectingWallet === item.id}
                        disabled={isConnecting && connectingWallet !== item.id}
                        onClick={() => handleMoreClick(item)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Bottom spacing */}
              <div className="h-2" />
            </>
          )}
        </div>
      </div>
    </>
  )
}
